import { createContext, createElement, useContext, useEffect, type ReactNode } from 'react';
import { useViewModel, useAuth, useAuthIsAnonymous } from '@footsteps/shared';
import { RegisterUserViewModel } from './register-user.viewmodel';
import type { RegisterUserState } from './register-user.state';
import type { RegisterUserProps } from './register-user.types';

type RegisterUserContextValue = {
  state: RegisterUserState;
  viewModel: Omit<RegisterUserViewModel, 'state'>;
};

const RegisterUserContext = createContext<RegisterUserContextValue | null>(null);

export const RegisterUserProvider = ({ children, ...props }: RegisterUserProps & { children: ReactNode }) => {
  const { user, linkEmail, verifyEmailOtp, resendEmailOtp, updateProfile, sendLoginOtp, verifyLoginOtp } = useAuth();

  const [state, viewModel] = useViewModel<RegisterUserState, RegisterUserViewModel>(() => new RegisterUserViewModel());

  useEffect(() => {
    viewModel.setAuth({ linkEmail, verifyEmailOtp, resendEmailOtp, updateProfile, sendLoginOtp, verifyLoginOtp });
  }, [viewModel, linkEmail, verifyEmailOtp, resendEmailOtp, updateProfile, sendLoginOtp, verifyLoginOtp]);

  useEffect(() => {
    viewModel.setProps(props);
  }, [viewModel, props.onVerifySuccess, props.showLogin]);

  const userId = user?.getId() ?? null;
  const isAnonymous = useAuthIsAnonymous();
  const userNewEmail = user?.new_email ?? null;
  const userEmailConfirmed = user?.email_confirmed_at ?? null;

  useEffect(() => {
    viewModel.determineStep(user ?? null);
  }, [viewModel, userId, isAnonymous, userNewEmail, userEmailConfirmed]);

  return createElement(RegisterUserContext.Provider, { value: { state, viewModel } }, children);
};

export const useRegisterUser = (): RegisterUserContextValue => {
  const context = useContext(RegisterUserContext);
  if (!context) {
    throw new Error('useRegisterUser must be used within a RegisterUserProvider');
  }
  return context;
};
